import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Text,
  Title,
  Table,
  Pagination,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconDroplet,
  IconBattery,
  IconTemperature,
  IconArrowBack,
  IconDownload,
} from "@tabler/icons-react";
import { SharedMeterDataType } from "../../types";
import {
  fetchSharedMeterReadingsAsync,
} from "../../actions/sharedUrl.actions";
import { formatNumberWithCommas } from "../../utils/numbers";
import { formatDate } from "../../utils/date";
import { getErrorCodeSummary } from "../../utils/errorCodes";
import DownloadFormatModal from "../../components/modals/DownloadFormatModal";
import generateSharedReadingsPdf from "./hooks/generateSharedReadingsPdf";
import generateSharedReadingsCsv from "./hooks/generateSharedReadingsCsv";
import { logReportDownloadAsync } from "../../actions/sharedUrl.actions";

type SharedMeterDetailsProps = {
  meter: SharedMeterDataType;
  token: string;
  onBack?: () => void;
};

export default function SharedMeterDetails({
  meter,
  token,
  onBack,
}: SharedMeterDetailsProps) {
  const [readings, setReadings] = useState<any[]>([]);
  const [loadingReadings, setLoadingReadings] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showLiters, setShowLiters] = useState(true);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);

  useEffect(() => {
    fetchReadings(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meter.id, token]);

  const fetchReadings = async (page: number) => {
    setLoadingReadings(true);
    try {
      const response = await fetchSharedMeterReadingsAsync(token, meter.id, page, 10);
      if (response?.data) {
        setReadings(response.data.data || []);
        setTotalPages(response.data.totalPages || 1);
        setCurrentPage(response.data.currentPage || 1);
      }
    } catch (err) {
      console.error("Error fetching readings:", err);
    } finally {
      setLoadingReadings(false);
    }
  };

  const handlePageChange = (page: number) => {
    fetchReadings(page);
  };

  const handleDownload = async (format: "pdf" | "csv") => {
    const response = await fetchSharedMeterReadingsAsync(token, meter.id, 1, 9999);
    const allReadings: any[] = response?.data?.data || [];
    if (format === "pdf") {
      generateSharedReadingsPdf({ meterSerialNo: meter.meterSerialNo, readings: allReadings, showLiters });
    } else {
      generateSharedReadingsCsv({ meterSerialNo: meter.meterSerialNo, readings: allReadings, showLiters });
    }
    logReportDownloadAsync({ meterSerialNo: meter.meterSerialNo, reportType: "shared-readings", format, sharedToken: token });
  };

  return (
    <Box>
      {onBack && (
        <Button
          variant="outline"
          color="gray"
          size="sm"
          leftSection={<IconArrowLeft size={16} />}
          onClick={onBack}
          mb="md"
        >
          Back to all meters
        </Button>
      )}

      <Card shadow="sm" padding="lg" radius="md" withBorder mb="lg">
        <Group justify="space-between" mb="md">
          <Group>
            <IconDroplet size={32} color="#0077b6" />
            <div>
              <Title order={3}>{meter.meterSerialNo}</Title>
              {meter.location && (
                <Text size="sm" c="dimmed">
                  {meter.location}
                </Text>
              )}
            </div>
          </Group>
          <Text size="sm" c="dimmed">
            Model: {meter.model}
          </Text>
        </Group>

        {meter.latestReading && (
          <Grid>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Paper p="md" radius="md" bg="#f0f9ff">
                <Text size="xs" c="dimmed">
                  Total Volume
                </Text>
                <Text size="xl" fw={700} c="#0077b6">
                  {formatNumberWithCommas(meter.latestReading.totalVolume)} L
                </Text>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Paper p="md" radius="md" bg="#f0fdf4">
                <Group gap={4}>
                  <IconBattery size={16} />
                  <Text size="xs" c="dimmed">
                    Battery
                  </Text>
                </Group>
                <Text size="xl" fw={700} c="#16a34a">
                  {meter.latestReading.battery}%
                </Text>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Paper p="md" radius="md" bg="#fef3c7">
                <Group gap={4}>
                  <IconTemperature size={16} />
                  <Text size="xs" c="dimmed">
                    Temperature
                  </Text>
                </Group>
                <Text size="xl" fw={700} c="#d97706">
                  {Number(meter.latestReading.temperature).toFixed(1)}°C
                </Text>
              </Paper>
            </Grid.Col>
            <Grid.Col span={{ base: 6, sm: 3 }}>
              <Paper p="md" radius="md" bg="#fdf2f8">
                <Group gap={4}>
                  <IconArrowBack size={16} />
                  <Text size="xs" c="dimmed">
                    Reverse Flow
                  </Text>
                </Group>
                <Text size="xl" fw={700} c="#db2777">
                  {formatNumberWithCommas(meter.latestReading.reverseFlow)} L
                </Text>
              </Paper>
            </Grid.Col>
          </Grid>
        )}

        <Text size="xs" c="dimmed" ta="right" mt="md">
          Last updated:{" "}
          {meter.latestReading
            ? formatDate(meter.latestReading.deviceTime)
            : "N/A"}
        </Text>
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={4}>Recent Readings</Title>
          <Group gap="sm">
            <SegmentedControl
              size="xs"
              value={showLiters ? "liters" : "units"}
              onChange={(val) => setShowLiters(val === "liters")}
              data={[
                { label: "Units", value: "units" },
                { label: "Liters", value: "liters" },
              ]}
            />
            <Button
              size="xs"
              variant="outline"
              color="gray"
              leftSection={<IconDownload size={14} />}
              onClick={() => setDownloadModalOpen(true)}
            >
              Download
            </Button>
          </Group>
        </Group>
        {loadingReadings ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text size="sm">Loading readings...</Text>
          </Group>
        ) : readings.length > 0 ? (
          <>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>{showLiters ? "Volume (l)" : "Volume (units)"}</Table.Th>
                  <Table.Th>Alarms</Table.Th>
                  <Table.Th>Battery</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {readings.map((reading, index) => {
                  const alarmSummary = getErrorCodeSummary(reading.errorCode || 0);
                  const hasAlarm = alarmSummary !== "No Errors";
                  const volumeDisplay = (() => {
                    if (showLiters) {
                      return (
                        <Text size="md" fw={700}>
                          {formatNumberWithCommas(Math.round(reading.totalVolume))}
                        </Text>
                      );
                    }
                    const truncated = Math.trunc(reading.totalVolume / 10) / 100;
                    const [whole, dec] = truncated.toFixed(2).split(".");
                    return (
                      <Group gap={0}>
                        <Text size="md" fw={700}>{formatNumberWithCommas(Number(whole))}</Text>
                        <Text size="sm" fw={400} c="dimmed">.{dec}</Text>
                      </Group>
                    );
                  })();
                  return (
                    <Table.Tr
                      key={index}
                      style={hasAlarm ? { backgroundColor: "#ffe6e6" } : undefined}
                    >
                      <Table.Td>{formatDate(reading.deviceTime)}</Table.Td>
                      <Table.Td>{volumeDisplay}</Table.Td>
                      <Table.Td>
                        <Text size="sm" c={hasAlarm ? "red" : "dimmed"}>
                          {hasAlarm ? alarmSummary : "None"}
                        </Text>
                      </Table.Td>
                      <Table.Td>{reading.battery}%</Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={currentPage}
                  onChange={handlePageChange}
                  size="sm"
                />
              </Group>
            )}
          </>
        ) : (
          <Text ta="center" c="dimmed" py="xl">
            No readings available
          </Text>
        )}
      </Card>

      <DownloadFormatModal
        opened={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={handleDownload}
      />
    </Box>
  );
}
